import api from '../../utils/api';
import { Vendor, SettlementStatus, VendorType } from '../../models/Accountant';

export interface VendorSettlementResponse {
  vendorId: string;
  vendorType: string;
  vendorName: string;
  clinicName?: string | null;
  doctorName?: string | null;
  email: string | null;
  phone: string | null;
  totalEarnings: number;
  platformCommission: number;
  commissionRate?: number; // Average commission rate
  gst: number;
  netPayable: number;
  totalOfferDiscount?: number;
  totalBaseCommission?: number;
  totalOfferAbsorbed?: number;
  totalRefunds: number;
  totalGatewayFees: number;
  transactionCount: number;
  pendingAmount: number;
  settledAmount: number;
  onHoldAmount: number;
  canSettle?: boolean;
  settlementStatus: string;
  lastSettlementDate: string | null;
  lifecycleStatus?: string | null;
}

export interface VendorSettlementsApiResponse {
  success: boolean;
  message: string;
  data: VendorSettlementResponse[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface VendorLedgerDetailsResponse {
  success: boolean;
  message: string;
  data: {
    vendor: {
      vendorId: string;
      vendorName: string;
      clinicName?: string | null;
      doctorName?: string | null;
      email: string | null;
      phone: string | null;
      vendorType: string;
    };
    totalCharges?: number;
    totalEarnings?: number;
    pendingSettlement?: number;
    onHold?: number;
    settleableAmount?: number;
    settleableCount?: number;
    settleableLedgerIds?: string[];
    waitingT2Amount?: number;
    waitingT2Count?: number;
    canSettle?: boolean;
    payoutPolicy?: {
      label: string;
      holdDays: number;
      description: string;
    };
    offerSummary?: {
      totalOfferDiscount: number;
      totalBaseCommission: number;
      totalPlatformCommission: number;
      totalOfferAbsorbed: number;
      offerEntryCount: number;
    };
    ledgerEntries: Array<{
      ledgerId: string;
      isSettlementEligible?: boolean;
      settlementEligibleAt?: string | null;
      daysUntilSettlementEligible?: number;
      [key: string]: unknown;
    }>;
    settlementQueue?: Array<{
      ledgerId: string;
      [key: string]: unknown;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

export interface FinancialOverviewResponse {
  success: boolean;
  message: string;
  data: {
    totalRevenue: {
      today: number;
      week: number;
      month: number;
    };
    pendingSettlements: number;
    completedSettlements: number;
    platformFeesCollected: number;
    gstCollected: number;
    failedSettlements: number;
  };
}

/**
 * Get all vendors with aggregated settlement details
 */
export const getVendorsWithSettlements = async (
  params?: {
    status?: SettlementStatus | 'pending' | 'on_hold' | 'settled';
    vendorType?: VendorType | string;
    lifecycleStatus?: string;
    search?: string;
    page?: number;
    limit?: number;
  }
): Promise<VendorSettlementsApiResponse> => {
  try {
    const response = await api.get('/master-admin/accountant/vendors', {
      params: {
        status: params?.status?.toLowerCase(),
        vendorType: params?.vendorType,
        lifecycleStatus: params?.lifecycleStatus,
        search: params?.search,
        page: params?.page || 1,
        limit: params?.limit || 50,
      },
    });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching vendors with settlements:', error);
    throw error;
  }
};

/**
 * Get detailed ledger entries for a specific vendor
 */
export const getVendorLedgerDetails = async (
  vendorId: string,
  params?: {
    status?: string;
    lifecycleStatus?: string;
    entryType?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }
): Promise<VendorLedgerDetailsResponse> => {
  try {
    const response = await api.get(`/master-admin/accountant/vendors/${vendorId}/ledger`, {
      params: {
        status: params?.status,
        lifecycleStatus: params?.lifecycleStatus,
        entryType: params?.entryType,
        startDate: params?.startDate,
        endDate: params?.endDate,
        page: params?.page || 1,
        limit: params?.limit || 50,
      },
    });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching vendor ledger details:', error);
    throw error;
  }
};

/**
 * Get financial overview dashboard data
 */
export const getFinancialOverview = async (
  period?: 'today' | 'week' | 'month'
): Promise<FinancialOverviewResponse> => {
  try {
    const response = await api.get('/master-admin/accountant/dashboard', {
      params: {
        period: period || 'month',
      },
    });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching financial overview:', error);
    throw error;
  }
};

export interface LedgerEntry {
  ledgerId: string;
  vendorId: string;
  vendorName: string;
  vendorType: string;
  serviceReferenceId: string;
  /** Medicine order id when row is derived from medicine flow (e.g. Borzo DP ledger). */
  medicineOrderId?: string | null;
  serviceType: string;
  grossAmount: number;
  mrp?: number;
  discountAmount?: number;
  discountRate?: number;
  baseCommissionAmount?: number;
  commissionRate: number;
  commissionAmount: number;
  gstOnCommission: number;
  gatewayFeeRecovered: number;
  deliveryCharges: number;
  refundAmount: number;
  internalCompensationAmount?: number;
  netPayable: number;
  status: string;
  lifecycleStatus?: string | null;
  entryType: string;
  deliveredBy: string | null;
  serviceCompletedAt: string | null;
  settledAt: string | null;
  settlementRef: string | null;
  payment: any;
  metadata: any & {
    ledgerSourceNote?: string;
  };
  /** delivery_partner_ledger | derived_from_medical_store */
  ledgerSource?: string;
}

export interface PlatformStats {
  totalRevenue: number;
  totalCommissions: number;
  totalOfferDiscount?: number;
  totalBaseCommission?: number;
  totalOfferAbsorbed?: number;
  netBalance: number;
  totalEntries: number;
  totalRefunds: number;
  totalGatewayFees: number;
}

export interface AllLedgerEntriesResponse {
  success: boolean;
  message: string;
  data: {
    ledgerEntries: LedgerEntry[];
    platformStats: PlatformStats;
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Get all ledger entries with filters (platform-wide)
 */
export const getAllLedgerEntries = async (
  params?: {
    vendorId?: string;
    vendorType?: string;
    serviceType?: string;
    status?: string;
    lifecycleStatus?: string;
    entryType?: string;
    startDate?: string;
    endDate?: string;
    search?: string;
    page?: number;
    limit?: number;
  }
): Promise<AllLedgerEntriesResponse> => {
  try {
    const response = await api.get('/master-admin/accountant/ledger', {
      params: {
        vendorId: params?.vendorId,
        vendorType: params?.vendorType,
        serviceType: params?.serviceType,
        status: params?.status,
        lifecycleStatus: params?.lifecycleStatus,
        entryType: params?.entryType,
        startDate: params?.startDate,
        endDate: params?.endDate,
        search: params?.search,
        page: params?.page || 1,
        limit: params?.limit || 50,
      },
    });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching ledger entries:', error);
    throw error;
  }
};

/**
 * Delivery partner ledger only (backend fixes vendorType = DELIVERY_PARTNER).
 */
export const getDeliveryPartnerLedgerEntries = async (
  params?: {
    vendorId?: string;
    status?: string;
    entryType?: string;
    startDate?: string;
    endDate?: string;
    search?: string;
    page?: number;
    limit?: number;
  }
): Promise<AllLedgerEntriesResponse> => {
  try {
    const response = await api.get('/master-admin/accountant/ledger/delivery-partner', {
      params: {
        vendorId: params?.vendorId,
        status: params?.status,
        entryType: params?.entryType,
        startDate: params?.startDate,
        endDate: params?.endDate,
        search: params?.search,
        page: params?.page || 1,
        limit: params?.limit || 50,
      },
    });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching delivery partner ledger:', error);
    throw error;
  }
};

export interface SettleVendorRequest {
  settlementRef?: string;
  remarks?: string;
  ledgerIds?: string[];
  bankDetails?: {
    accountNumber?: string;
    ifscCode?: string;
    bankName?: string;
    accountHolderName?: string;
  };
}

export interface SettleVendorResponse {
  success: boolean;
  message: string;
  data: {
    vendorId: string;
    vendorName: string;
    totalAmount: number;
    entryCount: number;
    settlementRef: string;
    settledAt: string;
    remarks: string | null;
  };
}

/**
 * Settle pending amounts for a vendor
 */
export const settleVendorAmount = async (
  vendorId: string,
  data?: SettleVendorRequest
): Promise<SettleVendorResponse> => {
  try {
    const response = await api.post(`/master-admin/accountant/vendors/${vendorId}/settle`, data || {});
    return response.data;
  } catch (error: any) {
    console.error('Error settling vendor amount:', error);
    throw error;
  }
};

/**
 * Payment entry interface
 */
export interface PaymentEntry {
  paymentId: string;
  userId: string;
  serviceType: string;
  serviceRefId: string | null;
  razorpayOrderId: string;
  razorpayPaymentId: string | null;
  amount: number;
  currency: string;
  status: 'created' | 'paid' | 'failed';
  verifiedAt: string | null;
  refundedAmount: number;
  createdAt: string;
  updatedAt: string;
}

export interface PaymentsResponse {
  success: boolean;
  message: string;
  data: {
    payments: PaymentEntry[];
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Gateway charge entry interface
 */
export interface GatewayChargeEntry {
  paymentId: string | null;
  serviceRefId: string | null;
  serviceType: string | null;
  razorpayOrderId: string | null;
  paymentAmount: number;
  paymentStatus: string | null;
  gatewayCharges: number;
  gstOnGateway: number;
  totalGatewayCost: number;
  charges: Array<{
    gatewayChargeId: string;
    chargeType: string;
    amount: number;
    gateway: string;
    remarks: string | null;
    createdAt: string;
  }>;
  createdAt: string | null;
}

export interface GatewayChargesResponse {
  success: boolean;
  message: string;
  data: {
    gatewayCharges: GatewayChargeEntry[];
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Get all payments with filters
 */
export const getAllPayments = async (
  params?: {
    serviceType?: string;
    status?: 'created' | 'paid' | 'failed';
    startDate?: string;
    endDate?: string;
    search?: string;
    page?: number;
    limit?: number;
  }
): Promise<PaymentsResponse> => {
  try {
    const response = await api.get('/master-admin/accountant/payments', {
      params: {
        serviceType: params?.serviceType,
        status: params?.status,
        startDate: params?.startDate,
        endDate: params?.endDate,
        search: params?.search,
        page: params?.page || 1,
        limit: params?.limit || 50,
      },
    });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching payments:', error);
    throw error;
  }
};

/**
 * Get all gateway charges with filters
 */
export const getAllGatewayCharges = async (
  params?: {
    chargeType?: string;
    gateway?: string;
    startDate?: string;
    endDate?: string;
    search?: string;
    page?: number;
    limit?: number;
  }
): Promise<GatewayChargesResponse> => {
  try {
    const response = await api.get('/master-admin/accountant/gateway-charges', {
      params: {
        chargeType: params?.chargeType,
        gateway: params?.gateway,
        startDate: params?.startDate,
        endDate: params?.endDate,
        search: params?.search,
        page: params?.page || 1,
        limit: params?.limit || 50,
      },
    });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching gateway charges:', error);
    throw error;
  }
};

/**
 * Payout entry interface
 */
export interface PayoutEntry {
  payoutId: string;
  payoutType: 'VENDOR' | 'DELIVERY_PARTNER' | 'USER';
  entityId: string;
  entityName: string;
  entityEmail: string | null;
  entityPhone: string | null;
  amount: number;
  vendorSettlementLedgerIds: string[]; // Array of ledger entry IDs
  refundId: string | null;
  payoutMethod: 'BANK' | 'RAZORPAY';
  referenceNumber: string | null;
  status: 'pending' | 'completed' | 'failed';
  remarks: string | null;
  orderIds: string[]; // Array of orderIds associated with this payout
  createdAt: string;
  updatedAt: string;
}

export interface PayoutStats {
  totalPayouts: number;
  totalAmount: number;
  pendingCount: number;
  completedCount: number;
  failedCount: number;
}

export interface PayoutsResponse {
  success: boolean;
  message: string;
  data: {
    payouts: PayoutEntry[];
    stats: PayoutStats;
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Get all payout ledger entries
 */
export const getAllPayouts = async (
  params?: {
    payoutType?: 'VENDOR' | 'DELIVERY_PARTNER' | 'USER';
    status?: 'pending' | 'completed' | 'failed';
    payoutMethod?: 'BANK' | 'RAZORPAY';
    entityId?: string;
    startDate?: string;
    endDate?: string;
    search?: string;
    page?: number;
    limit?: number;
  }
): Promise<PayoutsResponse> => {
  try {
    const response = await api.get('/master-admin/accountant/payouts', {
      params: {
        payoutType: params?.payoutType,
        status: params?.status,
        payoutMethod: params?.payoutMethod,
        entityId: params?.entityId,
        startDate: params?.startDate,
        endDate: params?.endDate,
        search: params?.search,
        page: params?.page || 1,
        limit: params?.limit || 50,
      },
    });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching payouts:', error);
    throw error;
  }
};

/**
 * Audit Log interfaces
 */
export interface AuditLogEntry {
  auditLogId: string;
  actionType: string;
  entityType: 'SETTLEMENT' | 'PAYOUT' | 'LEDGER';
  entityId: string;
  performedBy: string;
  performedByName: string | null;
  performedByRole: 'ADMIN' | 'ACCOUNTANT' | 'SYSTEM' | 'VENDOR' | null;
  vendorId: string | null;
  oldStatus: string | null;
  newStatus: string | null;
  oldAmount: number | null;
  newAmount: number | null;
  oldValues: Record<string, any> | null;
  newValues: Record<string, any> | null;
  remarks: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  performedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface AuditLogStats {
  totalLogs: number;
  initiatedCount: number;
  completedCount: number;
  retryCount: number;
  adjustmentCount: number;
}

export interface AuditLogsResponse {
  success: boolean;
  message: string;
  data: {
    auditLogs: AuditLogEntry[];
    stats: AuditLogStats;
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Get all audit logs
 */
export const getAuditLogs = async (
  params?: {
    actionType?: string;
    entityType?: 'SETTLEMENT' | 'PAYOUT' | 'LEDGER';
    entityId?: string;
    performedBy?: string;
    vendorId?: string;
    performedByRole?: 'ADMIN' | 'ACCOUNTANT' | 'SYSTEM' | 'VENDOR';
    startDate?: string;
    endDate?: string;
    search?: string;
    page?: number;
    limit?: number;
  }
): Promise<AuditLogsResponse> => {
  try {
    const response = await api.get('/master-admin/accountant/audit-logs', {
      params: {
        actionType: params?.actionType,
        entityType: params?.entityType,
        entityId: params?.entityId,
        performedBy: params?.performedBy,
        vendorId: params?.vendorId,
        performedByRole: params?.performedByRole,
        startDate: params?.startDate,
        endDate: params?.endDate,
        search: params?.search,
        page: params?.page || 1,
        limit: params?.limit || 50,
      },
    });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching audit logs:', error);
    throw error;
  }
};

/**
 * Get audit logs for a specific entity
 */
export const getEntityAuditLogs = async (
  entityType: 'SETTLEMENT' | 'PAYOUT' | 'LEDGER',
  entityId: string,
  params?: {
    page?: number;
    limit?: number;
  }
): Promise<AuditLogsResponse> => {
  try {
    const response = await api.get(`/master-admin/accountant/audit-logs/${entityType}/${entityId}`, {
      params: {
        page: params?.page || 1,
        limit: params?.limit || 50,
      },
    });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching entity audit logs:', error);
    throw error;
  }
};

/**
 * Financial Dashboard interfaces
 */
export interface FinancialDashboardResponse {
  success: boolean;
  message: string;
  data: {
    totalRevenue: {
      today: number;
      week: number;
      month: number;
      custom?: number;
    };
    totalCommission: number;
    platformFeesCollected: number;
    gstCollected: number;
    totalRecoveredCharges: number;
    pendingSettlements: number;
    completedSettlements: number;
    failedSettlements: number;
    upcomingSettlementCycle: {
      date: string;
      estimatedAmount: number;
      vendorCount: number;
    };
  };
}

/**
 * Get financial dashboard data
 */
export const getFinancialDashboard = async (params?: {
  period?: 'daily' | 'weekly' | 'monthly' | 'custom';
  startDate?: string;
  endDate?: string;
}): Promise<FinancialDashboardResponse> => {
  try {
    const response = await api.get('/master-admin/accountant/dashboard', { params });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching financial dashboard:', error);
    throw error;
  }
};

/**
 * Order Ledger Interfaces
 */
export interface OrderLedgerPayment {
  paidAmount: number;
  paymentMethod: string | null;
  paymentDate: string | null;
  paymentStatus: string | null;
}

export interface OrderLedgerCustomerAccount {
  medicinesAmount: number;
  transportAmount: number;
  consultationFee?: number;
  discountAmount?: number;
  totalPaid: number;
  refundedAmount: number;
}

export interface OrderLedgerVendorMedical {
  grossAmount: number;
  discount: number;
  commission: number;
  gstOnCommission: number;
  refundImpact: number;
  netPayable: number;
  amountReceived?: number;
  settlementStatus: string | null;
}

export interface OrderLedgerVendorTransportation {
  transportCharge: number;
  deliveryPartnerPay: number;
  settlementStatus: string | null;
}

export interface OrderLedgerRazorpayAndBank {
  gatewayCharges: number;
  gstOnGateway: number;
  totalGatewayCost: number;
  payoutToBank: number;
}

export interface OrderLedgerCommissionSummary {
  commissionEarned: number;
  gstPayable: number;
  totalCommissionWithGst: number;
}

export interface OrderLedgerProfitAndLoss {
  totalCommission: number;
  totalInternalCompensation: number;
  totalGatewayCharges: number;
  netProfit: number;
}

export interface OrderLedgerPayout {
  payoutType: string;
  entityId: string;
  amount: number;
  payoutMethod: string;
  referenceNumber: string | null;
  status: string;
}

export interface OrderLedgerAuditTrail {
  actionType: string;
  performedByName: string | null;
  performedByRole: string | null;
  performedAt: string;
  remarks: string | null;
}

export interface OrderLedgerMasterResponse {
  orderId: string;
  payment: OrderLedgerPayment;
  customerAccount: OrderLedgerCustomerAccount;
  vendorMedical: OrderLedgerVendorMedical;
  vendorTransportation: OrderLedgerVendorTransportation;
  razorpayAndBank: OrderLedgerRazorpayAndBank;
  commissionSummary: OrderLedgerCommissionSummary;
  profitAndLoss: OrderLedgerProfitAndLoss;
  settlementLedgers: Array<{
    ledgerId: string;
    vendorId: string;
    vendorType: string;
    serviceType: string;
    serviceRefId: string;
    paymentId: string | null;
    entryType: string;
    entrySource: string | null;
    lifecycleStatus: string | null;
    grossAmount: number;
    commissionRate: number;
    commissionAmount: number;
    gstOnCommission: number;
    gatewayFeeRecovered: number;
    refundAmount: number;
    internalCompensationAmount: number;
    externalCompensationAmount: number;
    netPayable: number;
    status: string;
    settledAt: string | null;
    settlementRef: string | null;
    serviceCompletedAt: string | null;
    remarks: string | null;
    metadata: Record<string, any> | null;
    createdAt: string;
    updatedAt: string;
  }>;
  gatewayCharges: Array<{
    gatewayChargeId: string;
    paymentId: string | null;
    chargeType: string;
    amount: number;
    gateway: string;
    remarks: string | null;
    createdAt: string;
    updatedAt: string;
  }>;
  refunds: Array<{
    refundId: string;
    paymentId: string;
    amount: number;
    reason: string | null;
    status: string;
    initiatedBy: string | null;
    serviceType: string | null;
    serviceRefId: string | null;
    metadata: Record<string, any> | null;
    createdAt: string;
    updatedAt: string;
  }>;
  payouts: OrderLedgerPayout[];
  auditTrail: OrderLedgerAuditTrail[];
}

export interface OrderLedgerApiResponse {
  success: boolean;
  data: OrderLedgerMasterResponse;
}

/**
 * Get master order ledger (all sections)
 */
export const getOrderLedgerMaster = async (orderId: string): Promise<OrderLedgerApiResponse> => {
  try {
    const response = await api.get(`/master-admin/accountant/ledger/order/${orderId}`);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching order ledger master:', error);
    throw error;
  }
};

/**
 * Get payment ledger section
 */
export const getPaymentLedger = async (orderId: string): Promise<{ success: boolean; data: OrderLedgerPayment }> => {
  try {
    const response = await api.get(`/master-admin/accountant/ledger/order/${orderId}/payment`);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching payment ledger:', error);
    throw error;
  }
};

/**
 * Get customer account ledger section
 */
export const getCustomerAccountLedger = async (orderId: string): Promise<{ success: boolean; data: OrderLedgerCustomerAccount }> => {
  try {
    const response = await api.get(`/master-admin/accountant/ledger/order/${orderId}/customer-account`);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching customer account ledger:', error);
    throw error;
  }
};

/**
 * Get vendor medical ledger section
 */
export const getVendorMedicalLedger = async (orderId: string): Promise<{ success: boolean; data: OrderLedgerVendorMedical }> => {
  try {
    const response = await api.get(`/master-admin/accountant/ledger/order/${orderId}/vendor-medical`);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching vendor medical ledger:', error);
    throw error;
  }
};

/**
 * Get vendor transport ledger section
 */
export const getVendorTransportLedger = async (orderId: string): Promise<{ success: boolean; data: OrderLedgerVendorTransportation }> => {
  try {
    const response = await api.get(`/master-admin/accountant/ledger/order/${orderId}/vendor-transport`);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching vendor transport ledger:', error);
    throw error;
  }
};

/**
 * Get Razorpay/Bank ledger section
 */
export const getRazorpayBankLedger = async (orderId: string): Promise<{ success: boolean; data: OrderLedgerRazorpayAndBank }> => {
  try {
    const response = await api.get(`/master-admin/accountant/ledger/order/${orderId}/razorpay-bank`);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching Razorpay/Bank ledger:', error);
    throw error;
  }
};

/**
 * Get commission ledger section
 */
export const getCommissionLedger = async (orderId: string): Promise<{ success: boolean; data: OrderLedgerCommissionSummary }> => {
  try {
    const response = await api.get(`/master-admin/accountant/ledger/order/${orderId}/commission`);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching commission ledger:', error);
    throw error;
  }
};

/**
 * Get profit & loss ledger section
 */
export const getProfitLossLedger = async (orderId: string): Promise<{ success: boolean; data: OrderLedgerProfitAndLoss }> => {
  try {
    const response = await api.get(`/master-admin/accountant/ledger/order/${orderId}/profit-loss`);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching profit & loss ledger:', error);
    throw error;
  }
};

/**
 * Get audit trail ledger section
 */
export const getAuditTrailLedger = async (orderId: string): Promise<{ success: boolean; data: OrderLedgerAuditTrail[] }> => {
  try {
    const response = await api.get(`/master-admin/accountant/ledger/order/${orderId}/audit-trail`);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching audit trail:', error);
    throw error;
  }
};

export type SettlementReportFormat = 'excel' | 'pdf';

export interface SettlementReportDownloadParams {
  startDate: string;
  endDate: string;
}

const triggerBlobDownload = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

const parseDownloadFileName = (
  contentDisposition: string | undefined,
  fallback: string
): string => {
  if (!contentDisposition) return fallback;
  const match = /filename="?([^";\n]+)"?/i.exec(contentDisposition);
  return match?.[1]?.trim() || fallback;
};

const parseBlobErrorMessage = async (blob: Blob): Promise<string | null> => {
  try {
    const text = await blob.text();
    const parsed = JSON.parse(text) as { message?: string };
    return parsed.message || null;
  } catch {
    return null;
  }
};

export const downloadSettlementReport = async (
  format: SettlementReportFormat,
  params: SettlementReportDownloadParams
): Promise<{ fileName: string; rowCount: number }> => {
  const fallback = `vedika-settlement-report_${params.startDate}_to_${params.endDate}.${
    format === 'pdf' ? 'pdf' : 'xlsx'
  }`;

  try {
    const response = await api.get('/master-admin/accountant/reports/settlement/download', {
      params: {
        format,
        startDate: params.startDate,
        endDate: params.endDate,
      },
      responseType: 'blob',
    });

    const blob = response.data as Blob;
    if (blob.type?.includes('application/json')) {
      const message = await parseBlobErrorMessage(blob);
      throw new Error(message || 'Failed to download settlement report');
    }

    const fileName = parseDownloadFileName(
      response.headers['content-disposition'] as string | undefined,
      fallback
    );
    const rowCount = Number(
      response.headers['x-export-row-count'] ?? response.headers['X-Export-Row-Count'] ?? 0
    );

    triggerBlobDownload(blob, fileName);
    return { fileName, rowCount: Number.isFinite(rowCount) ? rowCount : 0 };
  } catch (error: any) {
    const blob = error?.response?.data as Blob | undefined;
    if (blob instanceof Blob && blob.type?.includes('application/json')) {
      const message = await parseBlobErrorMessage(blob);
      throw new Error(message || 'Failed to download settlement report');
    }
    throw error;
  }
};

/**
 * Map API response to Vendor model
 */
export const mapVendorSettlementToVendor = (
  apiVendor: VendorSettlementResponse
): Vendor & { pendingAmount?: number; canSettle?: boolean } => {
  // Use commissionRate from API (for MEDICAL_STORE vendors, this comes directly from VendorMedicalStoreProfile)
  // Fallback calculation only used if commissionRate is not provided (for other vendor types or edge cases)
  const commissionRate = apiVendor.commissionRate !== undefined && apiVendor.commissionRate !== null
    ? apiVendor.commissionRate
    : (apiVendor.totalEarnings > 0 
        ? (apiVendor.platformCommission / apiVendor.totalEarnings) * 100 
        : 0);

  return {
    id: apiVendor.vendorId,
    name: apiVendor.vendorName,
    clinicName: apiVendor.clinicName ?? undefined,
    doctorName: apiVendor.doctorName ?? undefined,
    vendorType: apiVendor.vendorType as VendorType,
    email: apiVendor.email || '',
    phone: apiVendor.phone || '',
    totalEarnings: apiVendor.totalEarnings,
    platformCommission: apiVendor.platformCommission,
    commissionRate: parseFloat(commissionRate.toFixed(2)),
    gst: apiVendor.gst,
    netPayable: apiVendor.netPayable,
    totalOfferDiscount: apiVendor.totalOfferDiscount,
    totalBaseCommission: apiVendor.totalBaseCommission,
    totalOfferAbsorbed: apiVendor.totalOfferAbsorbed,
    pendingAmount: apiVendor.pendingAmount,
    canSettle: apiVendor.canSettle ?? apiVendor.pendingAmount > 0,
    settlementStatus: apiVendor.settlementStatus as SettlementStatus,
    lastSettlementDate: apiVendor.lastSettlementDate || undefined,
  };
};
